import { Component, ElementRef, HostListener, Inject, OnInit, ViewChild } from '@angular/core';
import { trigger, state, style, transition, animate } from '@angular/animations';
import { HttpClient } from '@angular/common/http';
import { MatSort } from '@angular/material/sort';
import { AppService, GRSSResponse, GRSSUserRoles, ValueIdPair } from 'src/app/_services/app.service';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { DialogConfirmComponent } from 'src/app/dialog/dialog-confirm/dialog-confirm.component';
import { AddReportComponent } from './add-report/add-report.component';
import { Router } from '@angular/router';
import { ComponentCanDeactivate } from 'src/app/_guard/pending-changes.guard';
import { Observable } from 'rxjs';
import { ShowReportComponent } from './show-report/show-report.component';
import { TranslateService } from '@ngx-translate/core';

@Component({
    selector: 'app-reports',
    templateUrl: './reports.component.html',
    styleUrls: ['./reports.component.css'],
    animations: [
        trigger('slider', [
            state('*', style({
                height: 0
            })),
            state('show', style({
                height: '*'
            })),
            transition('* <=> show', [animate('400ms cubic-bezier(0.25, 0.8, 0.25, 1)')])
        ])
    ],
    standalone: false
})
export class ReportsComponent implements OnInit, ComponentCanDeactivate {
  reports: ValueIdPair[];
  selectedReportProperties: ReportProperties;
  selectedReportPropertiesTemp: ReportProperties;
  searchParameters: ReportSearchParameters;
  isSelectedReport: boolean = false;
  isGeneratedReport: boolean = false;
  dataSource;
  displayColumnsDynamic: string[] = [];
  displayFiltersDynamic: Column[] = [];
  displayColumnsFiltersDynamic: string[] = [];
  resultLength: number = 0;
  mattablePageSize = 30;
  lists: Lists;
  loading: boolean = false;
  outerTabGroupSelectedIndex: number = 0;
  innerTabGroupSelectedIndex: number = 0;
  displayColumnsPropColumns: string[] = [];
  dataSourcePropColumns;
  columnsLength: number = 0;
  tableList: string[] = [];
  columnList: TableList[] = [];
  sqlColumn: string = '';
  isSelectedTable: boolean = false;
  adminRoleName = GRSSUserRoles.Admin;
  operatorRoleName: string = GRSSUserRoles.Operator;
  canChangeAccount: boolean = false;

  @ViewChild(MatSort) sort: MatSort;
  @ViewChild('codeMirror', { static: true }) codemirror: ElementRef;

  hintOptionTables: any = {};

  constructor(private translate: TranslateService, @Inject('BASE_URL') private baseUrl: string, private http: HttpClient, public service: AppService, private dialog: MatDialog, private router: Router) {
    this.service.SetTranslation('administration/reports');
   }

  ngOnInit() {
    this.GetReports();
  }

  @HostListener('window:beforeunload')
  canDeactivate(): Observable<boolean> | boolean {
    // insert logic to check if there are pending changes here;
    // returning true will navigate without confirmation
    // returning false will show a confirm dialog before navigating away

    let hasChanges: boolean = false;

    if (JSON.stringify(this.selectedReportProperties) !== JSON.stringify(this.selectedReportPropertiesTemp)) {
      hasChanges = true;
    }

    return !hasChanges;
  }

  /** Initializes parameters used in the form */
  InitializeParameters() {
    this.searchParameters = {
      dateFrom: this.searchParameters ? this.searchParameters.dateFrom : new Date(),
      dateTo: this.searchParameters ? this.searchParameters.dateTo : new Date(),
      selectedAccount: 'all',
      selectedInstrument: 'all',
      selectedMarket: this.searchParameters ? this.searchParameters.selectedMarket : null,
      selectedSummary: null,
      properties: null
    };
    this.displayColumnsDynamic = [];
    this.displayFiltersDynamic = [];
    this.displayColumnsFiltersDynamic = [];
  }

  /** Gets reports details */
  GetReports() {
    this.http.get<ValueIdPair[]>(this.baseUrl + 'api/reports/getReports').subscribe(result => {
      this.reports = result;
      const currentRole = this.service.currentUser.userRole;
      if (currentRole === GRSSUserRoles.Admin || currentRole === GRSSUserRoles.Operator || currentRole === GRSSUserRoles.Compliance || currentRole === GRSSUserRoles.Staff) {
        this.canChangeAccount = true;
      }
    }, error => {
      console.error(error);
    });
  }

  /**
   * Get details of a selected report.
   * @param reportId Selected report id
   */
  GetReport(reportId: string) {
    this.http.get<ReportDetails>(this.baseUrl + 'api/reports/getReport/' + reportId).subscribe(result => {
      this.isSelectedReport = true;
      this.selectedReportProperties = result.properties;
      this.selectedReportPropertiesTemp = JSON.parse(JSON.stringify(result.properties));
      if (!this.selectedReportProperties.columns) {
        this.selectedReportProperties.columns = [];
        this.selectedReportPropertiesTemp.columns = [];
      }
      this.dataSourcePropColumns = this.selectedReportProperties.manageColumns;
      this.columnsLength = this.selectedReportProperties.manageColumns.length;
      this.dataSourcePropColumns.sort = this.sort;
      this.lists = result.lists;
      this.EnableDisableColumnCheckboxes();
      const optionsTables: any = {};
      if (this.lists.tableList.length > 0) {
        this.lists.tableList.forEach(table => {
          this.tableList.push(table.name);

          const cols: string[] = [];
          table.columns.forEach(column => {
            cols.push(column.value.replace(table.name, '').replace('.', '').replace('[', '').replace(']', ''));
          });
          optionsTables[table.name] = cols;
        });
      }
      if (this.selectedReportProperties.hasMarkets === true) {
        if (this.searchParameters.selectedMarket == null) {
          this.searchParameters.selectedMarket = this.lists.markets[0].id;
        }
        this.OnChangeMarket(this.searchParameters.selectedMarket);
      }
      const optionsTemp: any = {
        tables: optionsTables
      };
      this.hintOptionTables = optionsTemp;
      const currentRole = this.service.currentUser.userRole;
      if (!(currentRole === GRSSUserRoles.Admin || currentRole === GRSSUserRoles.Operator || currentRole === GRSSUserRoles.Compliance || currentRole === GRSSUserRoles.Staff)) {
        this.searchParameters.selectedAccount = this.service.currentUser.accountId;
      }
    }, error => {
      console.error(error);
    }, () => {
    });
  }

  /**
   * Click event for the report.
   */
  OnClickReport() {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.disableClose = true;
    dialogConfig.autoFocus = true;
    dialogConfig.position = { top: '5%' };
    dialogConfig.width = '90%';
    dialogConfig.height = '85%';
    dialogConfig.data = {
      selectedReportProperties: this.selectedReportProperties,
      searchParameters: this.searchParameters
    };
    this.dialog.open(ShowReportComponent, dialogConfig);
  }

  /**
   * On change event of a report
   * @param reportId Selected report
   */
  OnChangeReport(reportId: string) {
    this.InitializeParameters();
    this.GetReport(reportId);
  }

  /**
   * On change event of a market
   * @param marketId selected market
   */
  OnChangeMarket(marketId: string) {
    this.isGeneratedReport = false;
    if (marketId != null) {
      const markets: ValueIdPair[] = this.lists.markets;
      const tableList: TableList[] = this.lists.tableList;
      const roles: ValueIdPair[] = this.lists.roles;
      this.lists.instruments = [];
      this.lists.accounts = [];
      this.http.post<Lists>(`${this.baseUrl}api/reports/onMarketChange/${marketId}`, this.selectedReportProperties).subscribe(result => {
        this.lists = result;
        this.lists.markets = markets;
        this.lists.tableList = tableList;
        this.lists.roles = roles;
      }, error => {
        console.error(error);
      });
    }
  }

  /**
   * Click event for the market checkbox to sutomatically deselect the instruments when the market deseleted.
   */
  OnClickMarketCheckBox() {
    if (this.selectedReportProperties.hasMarkets === false) {
      this.selectedReportProperties.hasInstruments = false;
    }
  }

  /**
   * Click event for the market create.
   */
  OnCreateReport() {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.disableClose = true;
    dialogConfig.autoFocus = true;
    dialogConfig.position = { top: '5%' };
    this.dialog.open(AddReportComponent, dialogConfig).afterClosed().subscribe(res => {
      if (res) {
        this.GetReports();
        this.isSelectedReport = false;

        const report = this.reports.find(rep => rep.value === res);
        if (report) {
          this.GetReport(report.id);
          this.isSelectedReport = true;
          this.outerTabGroupSelectedIndex = 1;
          this.innerTabGroupSelectedIndex = 1;
        }

        this.isSelectedTable = false;
      }
    });
  }

  /**
   * Report delete button event.
   */
  OnDeleteReport() {
    if (this.service.currentUser.userRole !== GRSSUserRoles.Operator) {
      const dialogRef = this.dialog.open(DialogConfirmComponent, {
        disableClose: true,
        data: {
          title: this.translate.instant('Reports.Messages.msgHeaderDeleteReport'),
          message: this.translate.instant('Reports.Messages.msgParaDeleteReport')
        },
        position: { top: '5%' }
      });
      dialogRef.afterClosed().subscribe(res => {

        // Evaluate the users response.
        if (res === true) {
          this.service.showSpinner();
          this.http.delete<GRSSResponse>(this.baseUrl + 'api/reports/deleteReport/' + this.selectedReportProperties.reportId).subscribe(result => {
            if (result.statusCode === 4) {
              this.service.SaveStatus(true, 'Reports.Messages.msgHeaderDeleteReport', result.statusMessage);
            } else {
              if (result.statusCode === 1) {
                this.isSelectedReport = false;
                this.isSelectedTable = false;
                this.GetReports();
              }
              this.service.SaveStatus(false, 'Reports.Messages.msgHeaderDeleteReport', result.statusMessage);
            }
          }, error => {
            console.error(error);
          }).add(() => {
            this.service.hideSpinner();
          });
        }
      });
    }
  }

  /**
   * Save button event for the report.
   */
  OnSaveReport() {
    this.service.showSpinner();
    this.http.post<GRSSResponse>(this.baseUrl + 'api/reports/saveReport', this.selectedReportProperties).subscribe(result => {
      if (result.statusCode === 4) {
        this.service.SaveStatus(true, 'Reports.Messages.msgHeaderSave', result.statusMessage);
      } else if (result.statusCode === 1) {
        this.GetReports();
        this.GetReport(this.selectedReportProperties.reportId);
        this.service.SaveStatus(false, 'Reports.Messages.msgHeaderSave', result.statusMessage);
      } else {
        this.service.SaveStatus(false, this.translate.instant('Reports.Messages.msgHeaderSave'), result.statusMessage, true);
      }
    }, error => {
      console.error(error);
    }).add(() => {
      this.service.hideSpinner();
    });
  }

  /**
   * Change wvent for the table selection dropdown
   * @param value selected table
   * @param fromInt Indicator to indicate the element => table or column
   */
  OnSelectedValueChange(value: string, fromInt: number) {
    if (fromInt === 0) {
      this.columnList = [];
      const table = this.lists.tableList.find(ta => ta.name === value);

      if (table) {
        this.columnList = table.columns;
        this.sqlColumn = '';
      }
    } else if (fromInt === 1) {
      this.sqlColumn = value;
    }
  }

  /**
   * Checks and adds the selected role to column access roles
   * @param column Selected column
   * @param role Selected role
   * @param checked Checked
   */
  onChangeColumnRole(column: Column, role: ValueIdPair, checked: boolean) {
    if (checked) {
      if (column.accessRoles.includes(role.id) == false) {
        column.accessRoles.push(role.id);
      }
    } else {
      const index = column.accessRoles.indexOf(role.id);
      if (index > -1) {
        column.accessRoles.splice(index, 1);
      }
    }
  }

  /**
   * Remove unnecessary columns from table according to the properties.
   */
  EnableDisableColumnCheckboxes() {
    const collist: string[] = ['admin'];
    
    this.selectedReportProperties.accessRoles.forEach(el => {
      const role = this.lists.roles.find(e => e.id == el)
      if (role) {
        collist.push(role.value.toLowerCase())
      }
    });
    collist.sort();
    this.displayColumnsPropColumns = ['columnName', 'space'];
    this.displayColumnsPropColumns = this.displayColumnsPropColumns.concat(collist);
  }
}

/**
 * An interface to access report details response from server.
 */
interface Report {
  totalCount: number;
  data: any;
  columns: Column[];
}

/**
 * An interface to access report details.
 */
interface ReportDetails {
  properties: ReportProperties;
  lists: Lists;
}

/**
 * An interface to access report properties.
 */
interface ReportProperties {
  reportId: string;
  name: string;
  manageColumns: Column[];
  columns: Column[];
  sqlQuery: string;
  hasMarkets: boolean;
  hasInstruments: boolean;
  hasAccounts: boolean;
  accessRoles: string[];
}

/**
 * An interface to access list items.
 */
interface Lists {
  accounts: ValueIdPair[];
  instruments: ValueIdPair[];
  markets: ValueIdPair[];
  tableList: TableList[];
  roles: ValueIdPair[];
}

interface TableList {
  name: string;
  value: string;
  columns: TableList[];
}

/**
 * An interface to access report search parameters.
 */
interface ReportSearchParameters {
  properties: ReportProperties;
  dateFrom: Date;
  dateTo: Date;
  selectedMarket: string;
  selectedInstrument: string;
  selectedAccount: string;
  selectedSummary: boolean;
}

/**
 * An interface to access column details.
 */
interface Column {
  columnId: string;
  name: string;
  accessRoles: string[];
  enabled: boolean;
  type: string;
  filter: ColumnFilter;
}

/**
 * An interface to access filter details.
 */
interface ColumnFilter {
  filterValue: string;
  filterType: string;
  filterEnabled: boolean;
}

